import { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  Typography, 
  Button, 
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import Confetti from 'react-confetti';
import useWindowSize from '../../hooks/useWindowSize';
import { useNavigate } from 'react-router-dom';

const steps = [
  {
    label: '🎯 Topic Generator',
    description: "Welcome to Instation! Let's start by finding the perfect topic for your LinkedIn post. You've received 5 free credits for AI post generation and 5 credits for AI image generation.",
    action: 'Start with Topics',
    path: '/dashboard/topic-generator'
  },
  {
    label: '✍️ Create Your Post',
    description: "Now that you have your topic, let's craft an engaging LinkedIn post using our AI.",
    action: 'Create Post',
    path: '/dashboard/generate-post'
  },
  {
    label: '🎨 Add an Image',
    description: 'Finally, make your post stand out with an AI-generated image that matches your content.',
    action: 'Generate Image',
    path: '/dashboard/generate-image'
  }
];

const WelcomePopup = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(true);
  const { width, height } = useWindowSize();

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleAction = (index) => {
    if (index === steps.length - 1) {
      navigate(steps[index].path);
      onClose();
    } else {
      handleNext();
    }
  };

  return (
    <>
      {showConfetti && open && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={200}
          colors={['#0A66C2', '#4B9EFA', '#FFD700', '#FF6B6B']}
        />
      )}
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)'
          }
        }}
      >
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                color: '#0A66C2',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              Welcome to Instation! 🎉
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: '1.1rem' }}
            >
              Let's get you started with creating amazing LinkedIn content
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography sx={{ 
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography sx={{ mb: 2 }}>{step.description}</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleAction(index)}
                      sx={{ 
                        mr: 1,
                        bgcolor: '#0A66C2',
                        '&:hover': {
                          bgcolor: '#0952a5'
                        }
                      }}
                    >
                      {step.action}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WelcomePopup; 